/**
 * ZoneCreateModal
 *
 * useZonePolygonDrawing 의 onRequestFinalize 콜백에서 띄우는 모달.
 * 사용자가 Zone 의 그룹/이름을 확정하면 finalizeDrawing(input) 으로 polygon 생성.
 *
 * 디자인은 GroupCreateModal 의 RelationTypeManager 스타일을 재사용.
 */

import { useEffect, useState } from 'react'
import styles from './RelationTypeManager.module.css'

export interface ZoneCreateModalProps {
  isOpen: boolean
  /** 자동 추천 이름 (예: Zone-3) */
  suggestedName: string
  /** 그래프 내 기존 zone 이름 목록 — 중복 검사용 */
  existingZoneNames: string[]
  /** 점 개수 (참고 표시) */
  pointCount: number
  /** 추천 색상 (#hex). 미지정 시 DEFAULT_COLOR */
  suggestedColor?: string
  onConfirm: (input: { name: string; group: string; color: string }) => void
  onCancel: () => void
}

// 'zones' (plural lowercase) — ZONE_LAYER_NAME 과 동일. routeLayerToCategory
// 의 /^zones?$/i 패턴이 'zone' 카테고리로 매핑 → export 의 zones 버킷에 누적.
const DEFAULT_GROUP = 'zones'
const DEFAULT_COLOR = '#3A8EEE'

export function ZoneCreateModal({
  isOpen,
  suggestedName,
  existingZoneNames,
  pointCount,
  suggestedColor,
  onConfirm,
  onCancel,
}: ZoneCreateModalProps) {
  const [name, setName] = useState(suggestedName)
  const [group, setGroup] = useState(DEFAULT_GROUP)
  const [color, setColor] = useState(suggestedColor || DEFAULT_COLOR)
  const [validationError, setValidationError] = useState<string | null>(null)

  // 모달이 열릴 때마다 추천 이름/색상으로 리셋
  useEffect(() => {
    if (isOpen) {
      setName(suggestedName)
      setGroup(DEFAULT_GROUP)
      setColor(suggestedColor || DEFAULT_COLOR)
      setValidationError(null)
    }
  }, [isOpen, suggestedName, suggestedColor])

  if (!isOpen) return null

  const validate = (candidateName: string): string | null => {
    const trimmed = candidateName.trim()
    if (!trimmed) return '이름을 입력해주세요'
    if (existingZoneNames.includes(trimmed)) return `이미 존재하는 이름입니다: ${trimmed}`
    return null
  }

  const handleConfirm = () => {
    const err = validate(name)
    if (err) {
      setValidationError(err)
      return
    }
    onConfirm({
      name: name.trim(),
      group: group.trim() || DEFAULT_GROUP,
      color: (color || '').trim() || DEFAULT_COLOR,
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleConfirm()
    }
    if (e.key === 'Escape') {
      onCancel()
    }
  }

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Zone 생성"
        data-testid="zone-create-modal"
      >
        <div className={styles.header}>
          <h3 className={styles.title}>Zone 생성</h3>
          <button
            className={styles.closeButton}
            onClick={onCancel}
            aria-label="닫기"
          >
            &times;
          </button>
        </div>

        <div className={styles.body}>
          {/* Group (현재는 ZONE 고정. 향후 카테고리 분리 시 select 로 확장) */}
          <div className={styles.field}>
            <label className={styles.label}>그룹 (Layer)</label>
            <input
              type="text"
              value={group}
              onChange={(e) => setGroup(e.target.value)}
              onKeyDown={handleKeyDown}
              className={styles.input}
              placeholder={DEFAULT_GROUP}
              data-testid="zone-create-modal-group"
            />
            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4 }}>
              저장 시 layer 키로 사용됩니다 (기본: {DEFAULT_GROUP})
            </div>
          </div>

          {/* Name */}
          <div className={styles.field}>
            <label className={styles.label}>이름</label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setValidationError(null)
              }}
              onKeyDown={handleKeyDown}
              className={styles.input}
              placeholder={suggestedName}
              autoFocus
              data-testid="zone-create-modal-name"
            />
            {validationError && (
              <div
                style={{
                  fontSize: 12,
                  color: '#f87171',
                  marginTop: 4,
                }}
                role="alert"
              >
                {validationError}
              </div>
            )}
          </div>

          {/* Color picker — stroke color */}
          <div className={styles.field}>
            <label className={styles.label}>외곽선 색상</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                style={{
                  width: 44,
                  height: 32,
                  padding: 0,
                  border: '1px solid var(--color-border, #555)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  background: 'transparent',
                }}
                data-testid="zone-create-modal-color-picker"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                onKeyDown={handleKeyDown}
                className={styles.input}
                placeholder="#3A8EEE"
                style={{ flex: 1 }}
                data-testid="zone-create-modal-color-text"
              />
            </div>
          </div>

          <div
            style={{
              fontSize: 12,
              color: 'var(--color-text-secondary)',
              padding: '0 0 8px',
            }}
          >
            점 {pointCount}개로 다각형이 생성됩니다 (마지막 점이 첫 점과 자동 연결).
          </div>
        </div>

        <div className={styles.footer}>
          <button
            className={styles.cancelButton}
            onClick={onCancel}
            data-testid="zone-create-modal-cancel"
          >
            취소
          </button>
          <button
            className={styles.saveButton}
            onClick={handleConfirm}
            data-testid="zone-create-modal-confirm"
          >
            생성
          </button>
        </div>
      </div>
    </div>
  )
}

export default ZoneCreateModal
